import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faRobot, 
  faInfo, 
  faCode, 
  faCalendar,
  faCopy,
  faCheckCircle,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

const GeneralTab = ({ agentId, agentOverview }) => {
  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${type} copied to clipboard!`);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!agentOverview) {
    return (
      <div className="general-tab loading">
        <p>Loading agent information...</p>
      </div>
    );
  }

  return (
    <div className="general-tab">
      {/* Agent Summary Card */}
      <div className="agent-summary-card">
        <div className="summary-header">
          <div className="agent-icon-large">
            <FontAwesomeIcon icon={faRobot} />
          </div>
          <div className="agent-summary-info">
            <h2>{agentOverview.agent_name}</h2>
            <p className="agent-description">{agentOverview.description}</p>
            <div className="agent-badges">
              <span className="version-badge">v{agentOverview.version}</span>
              <span className={`status-badge ${agentOverview.is_active ? 'active' : 'inactive'}`}>
                <FontAwesomeIcon icon={agentOverview.is_active ? faCheckCircle : faTimesCircle} />
                {agentOverview.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        <div className="summary-stats">
          <div className="stat-card">
            <div className="stat-number">{agentOverview.prompt_count}</div>
            <div className="stat-label">Prompts</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{agentOverview.tool_count}</div>
            <div className="stat-label">Tools</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{agentOverview.is_active ? '100%' : '0%'}</div>
            <div className="stat-label">Uptime</div>
          </div>
        </div>
      </div>

      {/* Configuration Sections */}
      <div className="config-sections">
        {/* Basic Information */}
        <div className="config-section">
          <div className="section-header">
            <FontAwesomeIcon icon={faInfo} />
            <h3>Basic Information</h3>
          </div>
          <div className="config-grid">
            <div className="config-item">
              <label>Agent ID</label>
              <div className="copyable-value">
                <code className="agent-id">{agentOverview.agent_id}</code>
                <button 
                  className="copy-btn"
                  onClick={() => copyToClipboard(agentOverview.agent_id, 'Agent ID')}
                  title="Copy Agent ID"
                >
                  <FontAwesomeIcon icon={faCopy} />
                </button>
              </div>
            </div>
            <div className="config-item">
              <label>Agent Name</label>
              <span>{agentOverview.agent_name}</span>
            </div>
            <div className="config-item full-width">
              <label>Description</label>
              <span>{agentOverview.description}</span>
            </div>
            <div className="config-item">
              <label>Version</label>
              <span className="version-display">v{agentOverview.version}</span>
            </div>
            <div className="config-item">
              <label>Status</label>
              <span className={`status-display ${agentOverview.is_active ? 'active' : 'inactive'}`}>
                <FontAwesomeIcon icon={agentOverview.is_active ? faCheckCircle : faTimesCircle} />
                {agentOverview.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        {/* Workflow Configuration */}
        <div className="config-section">
          <div className="section-header">
            <FontAwesomeIcon icon={faCode} />
            <h3>Workflow Configuration</h3>
          </div>
          <div className="config-grid">
            {agentOverview.workflow_file_path && (
              <div className="config-item full-width">
                <label>Workflow File Path</label>
                <div className="file-path-display">
                  <code>{agentOverview.workflow_file_path}</code>
                </div>
              </div>
            )}
            <div className="config-item">
              <label>Prompt Count</label>
              <span className="count-display">{agentOverview.prompt_count} prompts</span>
            </div>
            <div className="config-item">
              <label>Tool Count</label>
              <span className="count-display">{agentOverview.tool_count} tools</span>
            </div>
          </div>
        </div>

        {/* Timestamps */}
        <div className="config-section">
          <div className="section-header">
            <FontAwesomeIcon icon={faCalendar} />
            <h3>Timeline</h3>
          </div>
          <div className="config-grid">
            <div className="config-item">
              <label>Created</label>
              <span className="date-display">
                {formatDate(agentOverview.created_at)}
              </span>
            </div>
            <div className="config-item">
              <label>Last Updated</label>
              <span className="date-display">
                {formatDate(agentOverview.updated_at)}
              </span>
            </div>
          </div>
        </div>

        {/* Agent Capabilities */}
        <div className="config-section">
          <div className="section-header">
            <FontAwesomeIcon icon={faRobot} />
            <h3>Agent Capabilities</h3>
          </div>
          <div className="capabilities-list">
            <div className="capability-item">
              <FontAwesomeIcon icon={faCheckCircle} className="capability-icon active" />
              <div className="capability-details">
                <h4>Bill Processing</h4>
                <p>Extract and process data from bill PDFs</p>
              </div>
            </div>
            <div className="capability-item">
              <FontAwesomeIcon icon={faCheckCircle} className="capability-icon active" />
              <div className="capability-details">
                <h4>NetSuite Integration</h4>
                <p>Create bills and manage vendor data in NetSuite</p>
              </div>
            </div>
            <div className="capability-item">
              <FontAwesomeIcon icon={faCheckCircle} className="capability-icon active" />
              <div className="capability-details">
                <h4>Purchase Order Matching</h4>
                <p>Match bills to existing purchase orders</p>
              </div>
            </div>
            <div className="capability-item">
              <FontAwesomeIcon icon={faCheckCircle} className="capability-icon active" />
              <div className="capability-details">
                <h4>Vendor Management</h4>
                <p>Search and validate vendor information</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="config-section">
          <div className="section-header">
            <FontAwesomeIcon icon={faCode} />
            <h3>Quick Actions</h3>
          </div>
          <div className="quick-actions">
            <button 
              className="action-button"
              onClick={() => toast.info('Feature coming soon')}
            >
              Test Agent Connection
            </button>
            <button 
              className="action-button"
              onClick={() => toast.info('Feature coming soon')}
            >
              View Execution Logs
            </button>
            <button 
              className="action-button"
              onClick={() => toast.info('Feature coming soon')}
            >
              Reset Agent Cache
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralTab;